"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DashboardRepository = void 0;
const DashboardQuotaReached_1 = require("../errors/DashboardQuotaReached");
const FailedToCreateDashboard_1 = require("../errors/FailedToCreateDashboard");
const FailedToDecryptDashboardBeforeClient_1 = require("../errors/FailedToDecryptDashboardBeforeClient");
const FailedToFetchDashboardAccessTokens_1 = require("../errors/FailedToFetchDashboardAccessTokens");
const FailedToUpdateDashboard_1 = require("../errors/FailedToUpdateDashboard");
const Permission_1 = require("./Permission");
const Repository_1 = require("./Repository");
class DashboardRepository {
    // private repo: { dashboards: Dashboard[]; permissions: DashboardPermission[] } | null;
    constructor(vizzly) {
        this.vizzly = vizzly;
        this.repo = new Repository_1.Repository('id');
    }
    createDashboard(createParams) {
        return __awaiter(this, void 0, void 0, function* () {
            let builtDashboard;
            if (this.vizzly.implementationStrategy.shouldPersistViaQueryEngine()) {
                const queryEngineAPI = this.vizzly.getVizzlyQueryEngineAPI();
                builtDashboard = yield queryEngineAPI.execute(queryEngineAPI.buildCreateDashboardRequest(createParams));
            }
            else {
                const vizzlyApi = this.vizzly.getVizzlyAPI();
                builtDashboard = yield vizzlyApi.execute(vizzlyApi.buildCreateDashboardRequest(createParams));
            }
            if (builtDashboard.status === 200) {
                const formattedPermissions = builtDashboard.body.permissions.map(Permission_1.Permission.formatDashboardPermission);
                const embellishedDashboard = DashboardRepository.embellishDashboardFromAPI(builtDashboard.body.dashboard, formattedPermissions);
                this.repo.addItem(embellishedDashboard, formattedPermissions);
                return {
                    dashboard: embellishedDashboard,
                    permissions: formattedPermissions,
                };
            }
            if (builtDashboard.status === 422) {
                throw new DashboardQuotaReached_1.DashboardQuotaReached(`Dashboard quota reached; ${JSON.stringify(builtDashboard.body)}`);
            }
            if (builtDashboard.status != 200) {
                throw new FailedToCreateDashboard_1.FailedToCreateDashboard(`Failed to create the dashboard from POST /api/v2/dashboard. Got ${builtDashboard.status}`);
            }
            throw new FailedToCreateDashboard_1.FailedToCreateDashboard(`Unknown failure. Got status ${builtDashboard.status}`);
        });
    }
    updateDashboard(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let updatedDashboard;
            if (this.vizzly.implementationStrategy.shouldPersistViaQueryEngine()) {
                const queryEngineAPI = this.vizzly.getVizzlyQueryEngineAPI();
                updatedDashboard = yield queryEngineAPI.execute(queryEngineAPI.buildUpdateDashboardRequest(params));
            }
            else {
                const vizzlyApi = this.vizzly.getVizzlyAPI();
                updatedDashboard = yield vizzlyApi.execute(vizzlyApi.buildUpdateDashboardRequest(params));
            }
            if (updatedDashboard.status === 200) {
                const embellishedDashboard = DashboardRepository.embellishDashboardFromAPI(updatedDashboard.body.dashboard, [
                    params.dashboardPermission,
                ]);
                this.repo.updateItem(embellishedDashboard);
                return embellishedDashboard;
            }
            else {
                throw new FailedToUpdateDashboard_1.FailedToUpdateDashboard(`Failed to update dashboard, got status ${updatedDashboard.status}`);
            }
        });
    }
    fetchDashboards(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.fetchDashboardAccessTokens(params);
        });
    }
    static embellishDashboardFromAPI(dashboard, permissions) {
        return Object.assign(Object.assign({}, dashboard), { permission: DashboardRepository.takeHighestPermission(dashboard.id, permissions) });
    }
    static takeHighestPermission(dashboardId, permissions) {
        const readPermissions = DashboardRepository.takePermissionsOfScope(dashboardId, permissions, 'read');
        const readWritePermissions = DashboardRepository.takePermissionsOfScope(dashboardId, permissions, 'read_write');
        return readWritePermissions[0] || readPermissions[0];
    }
    static takePermissionsOfScope(dashboardId, permissions, scope) {
        return permissions.filter((p) => p.dashboardId === dashboardId && p.scope === scope);
    }
    fetchDashboardAccessTokens(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let accessTokens;
            if (!this.repo.isPopulated()) {
                if (this.vizzly.implementationStrategy.shouldPersistViaQueryEngine()) {
                    const vizzlyQueryEngineApi = this.vizzly.getVizzlyQueryEngineAPI();
                    accessTokens = yield vizzlyQueryEngineApi.execute(vizzlyQueryEngineApi.buildFetchDashboardAccessTokensRequest(params));
                }
                else {
                    const vizzlyApi = this.vizzly.getVizzlyAPI();
                    accessTokens = yield vizzlyApi.execute(vizzlyApi.buildFetchDashboardAccessTokensRequest(params));
                }
                if (accessTokens.status === 200) {
                    const formattedPermissions = accessTokens.body.permissions.map(Permission_1.Permission.formatDashboardPermission);
                    const embellishedDashboards = accessTokens.body.dashboards.map((dashboard) => {
                        if (typeof dashboard.definition === 'string')
                            throw new FailedToDecryptDashboardBeforeClient_1.FailedToDecryptDashboardBeforeClient();
                        return DashboardRepository.embellishDashboardFromAPI(dashboard, formattedPermissions);
                    });
                    this.repo.initRepo(embellishedDashboards, formattedPermissions);
                }
                else {
                    throw new FailedToFetchDashboardAccessTokens_1.FailedToFetchDashboardAccessTokens('Failed to fetch dashboard access tokens.');
                }
            }
            return {
                dashboards: this.repo.getRepo().storedItems,
                permissions: this.repo.getRepo().permissions,
            };
        });
    }
    clearRepo() {
        this.repo.clear();
    }
}
exports.DashboardRepository = DashboardRepository;
